import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, GraduationCap } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0047AB] flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">Exam Trainer</span>
            </div>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 md:p-12 shadow-sm">
          <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8">Last updated: 10 February 2026</p>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">1. Introduction</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Welcome to Exam Trainer, operated by Margolytics Ltd. We are committed to protecting your personal data in accordance with the UK GDPR, the EU GDPR (where applicable), and other applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">2. Data Controller</h2>
              <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-lg">
                <p className="text-slate-700 dark:text-slate-300">
                  <strong>Margolytics Ltd.</strong><br />
                  128 City Road, London, EC1V 2NX, United Kingdom<br />
                  Email: <a href="mailto:info@margolytics.com" className="text-[#0047AB] hover:underline">info@margolytics.com</a><br />
                  Phone: +44 20 45 77 05 42<br />
                  Company Registration No.: 16239233
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">3. Data We Collect</h2>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2">
                <li>Personal identifiers (name, email)</li>
                <li>Account data (login credentials, certification track)</li>
                <li>Usage data (IP address, browser type, pages visited, learning progress)</li>
                <li>Exam-related data (answers, results, attempts)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">4. Purpose of Processing</h2>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2">
                <li>Providing and operating the platform</li>
                <li>Managing user accounts and learning progress</li>
                <li>Customer support and system notifications</li>
                <li>Improving platform quality and security</li>
                <li>Legal and regulatory compliance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">5. Legal Basis</h2>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2">
                <li>Consent</li>
                <li>Contract performance</li>
                <li>Legal obligation</li>
                <li>Legitimate interest</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">6. Third Parties</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                Data may be shared with:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2">
                <li>Hosting and infrastructure providers</li>
                <li>Email service providers</li>
                <li>AI-supported services used for explanations and translations</li>
                <li>Authorities where legally required</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mt-4">
                All processors comply with applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">7. International Users</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Where users access the platform from outside the UK or EEA, data is processed under UK data protection standards and EU GDPR principles where applicable.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">8. Data Retention</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Personal data is retained only as long as necessary or legally required.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">9. Your Rights</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                Access, rectification, erasure, restriction, portability, objection, withdrawal of consent.
              </p>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Contact: <a href="mailto:info@margolytics.com" className="text-[#0047AB] hover:underline">info@margolytics.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">10. Cookies</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                See <a href="/cookie-policy" className="text-[#0047AB] hover:underline">Cookie Policy</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">11. Security</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                We use appropriate technical and organizational security measures. No system is fully secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mt-8 mb-4">12. Updates</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Updates will be published with a revised date.
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
